import { ChangeEvent, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { UploadSimple } from "@phosphor-icons/react"
import { PayloadError } from "relay-runtime"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { Banner } from "@/components/ui/banner"
import { Button } from "@/components/ui/button"
import { Card, CardHeader, CardHeaderTitle } from "@/components/ui/card"
import { UserProvider } from "@/components/user-wrapper"
import { formatInServerTimeZone } from "@/lib/initial-time-zone"
import { useImportAtossFileMutation$data } from "@/modules/admin/capacity/services/__generated__/useImportAtossFileMutation.graphql"
import {
  fetchAtossFileUpload,
  useAtossFileUploadQuery,
} from "@/modules/admin/capacity/services/useAtossFileUploadQuery"
import { useImportAtossFileMutation } from "@/modules/admin/capacity/services/useImportAtossFileMutation"

const PROCESSING_FETCH_INTERVAL_IN_MS = 5000
const SUPPORT_FILE_TYPE = ".xml"
const ERRORS_TRANSLATION_MAP: Record<string, string> = {
  UploadError: "admin.capacity.atossFileUpload.errors.uploadFailed",
  DuplicateUpload: "admin.capacity.atossFileUpload.errors.duplicateUpload",
}

async function hashFile(file: File) {
  const hashBuffer = await crypto.subtle.digest("SHA-256", await file.arrayBuffer())
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, "0")).join("")
  return hashHex
}

// Here are the steps in the ATOSS import process, please read this if you are confused about the logic:
// 1. Uploading (call the mutation `setImportAtossFileMutation`)
// 2. Processing...
// 3. Import succeeded/failed (call the query `fetchAtossFileUpload` to get the result of the import)

const AtossFileUploadCard = () => {
  const { t } = useTranslation()
  const [setImportAtossFileMutation, isUploading] = useImportAtossFileMutation()
  const { atossFileUpload } = useAtossFileUploadQuery()

  const [uploadError, setUploadError] = useState<"DuplicateUpload" | "UploadError">()
  const [didUploadInThisSession, setDidUploadInThisSession] = useState(false)
  const hiddenFileInputRef = useRef<HTMLInputElement>(null)

  const { uploadedAt, uploadedBy, fileHash, processedStatus, processedOn, processedError } = atossFileUpload
  const isNewImportOnProcess = isUploading || processedStatus === "ONGOING"
  const shouldShowErrorBanner = !isNewImportOnProcess && didUploadInThisSession && (uploadError || processedError)
  const shouldShowLatestImportStatus = uploadedAt && uploadedBy && processedOn && !isNewImportOnProcess
  const finalError = uploadError || processedError || ""

  // Interval fetch the processing status after finishing the upload
  useEffect(() => {
    let intervalId: NodeJS.Timeout

    if (processedStatus === "ONGOING") {
      intervalId = setInterval(() => {
        fetchAtossFileUpload()
      }, PROCESSING_FETCH_INTERVAL_IN_MS)
    }

    return () => clearInterval(intervalId)
  }, [isUploading, processedStatus])

  const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
    setDidUploadInThisSession(true)

    const { files } = event.target
    if (!files?.length) return

    const [uploadFile] = files

    if (!uploadFile.name.endsWith(SUPPORT_FILE_TYPE)) {
      setUploadError("UploadError")
      hiddenFileInputRef.current!.value = ""
      return
    }

    const newFileHash = await hashFile(uploadFile)
    const wasFileAlreadyImported =
      processedStatus === "SUCCEEDED" && newFileHash.toUpperCase() === fileHash?.toUpperCase()
    if (wasFileAlreadyImported) {
      setUploadError("DuplicateUpload")
      hiddenFileInputRef.current!.value = ""
      return
    }

    setImportAtossFileMutation(
      { file: uploadFile },
      (response: useImportAtossFileMutation$data, errors: PayloadError[] | null) => {
        const errorList = errors ? errors : response.importAtossFile.errors
        if (errorList) {
          setUploadError("UploadError")
        } else {
          setUploadError(undefined)
        }
      },
      () => {
        setUploadError("UploadError")
      },
    )

    hiddenFileInputRef.current!.value = ""
  }

  const getLatestImportProcessingStatusText = () => {
    const result =
      processedStatus === "SUCCEEDED"
        ? t("admin.capacity.atossFileUpload.processedSuccessfullyOn")
        : t("admin.capacity.atossFileUpload.processedFailedOn")
    const time = formatInServerTimeZone(processedOn!, "dd.MM.yyyy HH:mm")
    const error = processedStatus === "FAILED" ? `: ${t(ERRORS_TRANSLATION_MAP[finalError]) || finalError}` : ""

    return `${result} ${time}${error}`
  }

  return (
    <Card className="h-fit w-92.5">
      <CardHeader>
        <CardHeaderTitle data-testid="card-header-title-content" className="flex items-center gap-4">
          {t("admin.capacity.atossFileUpload.title")}
        </CardHeaderTitle>
        <div className="flex justify-end">
          <Button
            data-testid="atoss-file-upload-button"
            variant="tertiary"
            isLoading={isNewImportOnProcess}
            className="p-2"
            onClick={() => hiddenFileInputRef.current?.click()}>
            <UploadSimple className="text-on-surface-subdued" />
            <input
              type="file"
              onChange={handleUpload}
              ref={hiddenFileInputRef}
              className="hidden"
              accept={SUPPORT_FILE_TYPE}
            />
          </Button>
        </div>
      </CardHeader>
      {(isNewImportOnProcess || shouldShowErrorBanner || shouldShowLatestImportStatus) && (
        <div className="grid items-center gap-4 p-4 pt-0">
          {isNewImportOnProcess && (
            <Banner
              data-testid="atoss-file-upload-ongoing"
              type="inline"
              variant="info"
              className="flex items-center gap-1 rounded-md">
              {t(
                isUploading ? "admin.capacity.atossFileUpload.uploading" : "admin.capacity.atossFileUpload.processing",
              )}
            </Banner>
          )}
          {shouldShowErrorBanner && (
            <Banner
              data-testid="atoss-file-upload-failed"
              type="inline"
              variant="critical"
              className="flex items-center gap-1 rounded-md">
              {t(ERRORS_TRANSLATION_MAP[finalError]) || finalError}
            </Banner>
          )}
          {shouldShowLatestImportStatus && (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <UserProvider
                  userName={uploadedBy}
                  returnMissingUser={false}
                  render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier="last-updated-atoss-file" />}
                />
                <div className="text-on-surface-subdued text-xs leading-4 font-normal tracking-[0.25px]">
                  {`${t("admin.capacity.atossFileUpload.uploadedOn")} ${formatInServerTimeZone(uploadedAt, "dd.MM.yyyy HH:mm")}`}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <UserProvider
                  userName={uploadedBy}
                  returnMissingUser={false}
                  render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier="last-updated-atoss-file" />}
                />
                <div
                  className="text-on-surface-subdued text-xs leading-4 font-normal tracking-[0.25px]"
                  data-testid="atoss-file-upload-status-message">
                  {getLatestImportProcessingStatusText()}
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </Card>
  )
}

export { AtossFileUploadCard }
